import { FormattedMessage, useIntl } from 'react-intl';
import Button from '@rio-cloud/rio-uikit/Button';
import Notification from '@rio-cloud/rio-uikit/Notification';

import { useLazyGetScheduleTemplatesResolvedQuery } from '@/services/dfpFrontendApi';
import { useTriggerConfigMessageMutation } from '@/services/triggerDfpMessageApi';
import type { ScheduleSummary } from '../../types';
import { formatCountdown, useCountdown } from '../../utils/useCountdown';

interface ConfiguredMessageRowProps {
    vin: string;
    schedule: ScheduleSummary;
    onViewConfig: (schedule: ScheduleSummary) => void;
    onOpenInArbitrary: (schedule: ScheduleSummary, mergedJson: string, overriddenJsonPaths: string[]) => void;
    isNearest?: boolean;
}

const ConfiguredMessageRow = ({
    vin,
    schedule,
    onViewConfig,
    onOpenInArbitrary,
    isNearest,
}: ConfiguredMessageRowProps) => {
    const intl = useIntl();
    const [triggerMessage, { isLoading }] = useTriggerConfigMessageMutation();
    const [fetchResolvedTemplate, { isFetching: isFetchingTemplate }] = useLazyGetScheduleTemplatesResolvedQuery();
    const countdown = useCountdown(schedule.cron);

    const variantLabel =
        schedule.variants.length === 1
            ? schedule.variants[0]
            : `${schedule.variants[0]} (+${schedule.variants.length - 1})`;

    return (
        <div className="panel panel-default margin-bottom-5">
            <div className="panel-body display-flex align-items-center justify-content-between padding-y-10">
                <div className="display-flex align-items-center gap-15 flex-1-1">
                    <div
                        className="width-45 height-45 bg-highlight-decent rounded display-flex align-items-center
                        justify-content-center border border-color-light flex-0-0-auto"
                    >
                        <span className="text-size-16 text-bold text-color-primary">{schedule.cid}</span>
                    </div>
                    <div className="flex-1-1">
                        <div className="text-size-14 text-bold margin-bottom-2">{variantLabel}</div>
                        <div className="display-flex align-items-center gap-10 flex-wrap">
                            <span className="display-flex align-items-center gap-3">
                                <span className="uikit-icon icon-files text-color-gray text-size-12" />
                                <span className="font-family-mono text-size-11 text-color-gray">
                                    cid{schedule.cid}/v{schedule.version}.protobuf
                                </span>
                                <span className="label label-default label-condensed">v{schedule.version}</span>
                            </span>
                            <span className="display-flex align-items-center gap-3">
                                <span className="uikit-icon icon-flash text-color-warning text-size-12" />
                                <span className="text-size-11 text-bold text-color-gray">
                                    {schedule.overrides_count} <FormattedMessage id="intl-msg:messageRow.overrides" />
                                </span>
                            </span>
                            <span className="display-flex align-items-center gap-3">
                                <span className="uikit-icon icon-time text-color-gray text-size-12" />
                                <span className="font-family-mono text-size-11 text-color-gray">{schedule.cron}</span>
                            </span>
                        </div>
                    </div>
                    <div
                        className="text-center padding-x-15 border-left border-color-lighter"
                        style={{ minWidth: 100 }}
                    >
                        <div className="text-size-10 text-bold text-color-gray text-uppercase margin-bottom-3">
                            <FormattedMessage id="intl-msg:messageRow.nextTrigger" />
                        </div>
                        <span className={`label font-family-mono ${isNearest ? 'label-warning' : 'label-default'}`}>
                            {formatCountdown(countdown)}
                        </span>
                    </div>
                </div>
                <div className="display-flex gap-5 margin-left-15">
                    <Button variant="outline" bsSize="sm" onClick={() => onViewConfig(schedule)}>
                        <span className="uikit-icon icon-detail-view margin-right-3" />
                        <FormattedMessage id="intl-msg:messageRow.viewConfig" />
                    </Button>
                    <Button
                        variant="outline"
                        bsSize="sm"
                        disabled={isFetchingTemplate}
                        onClick={async () => {
                            try {
                                const result = await fetchResolvedTemplate({ vin, index: schedule.index }).unwrap();
                                onOpenInArbitrary(schedule, result.merged, result.overridden_json_paths);
                            } catch {
                                Notification.error(intl.formatMessage({ id: 'intl-msg:common.error.failedToLoad' }));
                            }
                        }}
                    >
                        <span className="uikit-icon icon-send margin-right-3" />
                        <FormattedMessage id="intl-msg:messageRow.openInArbitrary" />
                    </Button>
                    <Button
                        bsSize="sm"
                        disabled={isLoading}
                        style={{ minWidth: 90 }}
                        onClick={async () => {
                            try {
                                await triggerMessage({ vin, messageIndex: schedule.index }).unwrap();
                                Notification.success(`Sent ${variantLabel} for ${vin}`);
                            } catch (err: unknown) {
                                const status = (err as { status?: number })?.status;
                                const msg =
                                    status === 403
                                        ? intl.formatMessage({ id: 'intl-msg:common.error.insufficientPermissions' })
                                        : intl.formatMessage(
                                              { id: 'intl-msg:common.error.failedToSend' },
                                              { variant: variantLabel, vin },
                                          );
                                Notification.error(msg);
                            }
                        }}
                    >
                        <span className="uikit-icon icon-play margin-right-3" />
                        {isLoading
                            ? intl.formatMessage({ id: 'intl-msg:common.sending' })
                            : intl.formatMessage({ id: 'intl-msg:messageRow.trigger' })}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default ConfiguredMessageRow;
